import classNames from 'classnames';

import { Card, Link, Text } from '@spotify-internal/encore-web';

import { useAuth } from '../../behaviors/auth-factory';
import { useSupportMessenger } from '../../hooks/useSupportMessenger';

import styles from './NotFoundContent.module.css';

const COPY_BY_VARIANT = {
  page: {
    title: 'Page not found',
    description:
      "Oops! Looks like you've ventured off the beaten path. The page you're looking for might have been moved, deleted, or never existed. Don't worry, it happens to the best of us!",
  },
  resource: {
    title: 'Resource not found',
    description:
      "Oops! The resource you're looking for might have been moved, deleted, or never existed. Don't worry, it happens to the best of us!",
  },
} as const;

export type NotFoundContentProps = {
  variant?: 'page' | 'resource';
  fillViewport?: boolean;
};

export const NotFoundContent = ({
  variant = 'page',
  fillViewport = true,
}: NotFoundContentProps = {}) => {
  const { user } = useAuth();
  const { openChat } = useSupportMessenger();

  const { title, description } = COPY_BY_VARIANT[variant];

  const handleContactSupport = () => {
    openChat(
      user?.name && user?.email
        ? { name: user.name, email: user.email }
        : undefined,
    );
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.fillViewport]: fillViewport,
      })}
    >
      <div className={styles.content}>
        <div className={styles.heading}>
          <Text variant="bodySmallBold" semanticColor="textSubdued">
            Error 404
          </Text>
          <Text variant="titleSmall" as="h2">
            {title}
          </Text>
        </div>
        <Text variant="bodyMedium" as="p" className={styles.description}>
          {description}
        </Text>
        <div className={styles.cards}>
          <Card
            id="not-found-check-url"
            className={styles.card}
            variant="elevated"
            padding="base"
            title="Double-check the URL"
            body={
              <Text variant="bodyMedium" as="p" semanticColor="textSubdued">
                Sometimes a simple typo is the culprit
              </Text>
            }
          />
          <Card
            id="not-found-start-fresh"
            className={styles.card}
            variant="elevated"
            padding="base"
            title="Start fresh"
            body={
              <Text variant="bodyMedium" as="p" semanticColor="textSubdued">
                <Link href="/" data-track-source-anchor="8caeaba7ab">Go back to the homepage</Link> and find what you
                need
              </Text>
            }
          />
          <Card
            id="not-found-need-help"
            className={styles.card}
            variant="elevated"
            padding="base"
            title="Need help?"
            body={
              <Text variant="bodyMedium" as="p" semanticColor="textSubdued">
                If you think this is an error, please{' '}
                <Link
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    handleContactSupport();
                  }}
                 data-track-source-anchor="86420add44">
                  contact our support team
                </Link>
              </Text>
            }
          />
        </div>
      </div>
    </div>
  );
};
